import {
  CardPrinting,
  FinishEnum,
  Legalities,
  ManaColorEnum,
} from "modules/cards/model/card";
import { EmbeddedCardPrice } from "modules/cards/model/card-price";
import {
  CollectionCard,
  SortOrderEnum,
} from "modules/collections/details/model/collection";
import {
  type GameId,
  PricingConfiguration,
  ShortUser,
} from "modules/common/model/models";
import { TradeListType } from "modules/tradeLists/model/tradeList";
import { z } from "zod";

export enum DeckLocationEnum {
  Mainboard,
  Sideboard,
  Companion,
  Commander,
  Considering,
  // Don is the One Piece DON!! pile — a separate stack of 10 DON!!
  // cards each player brings. Only OP decks render this section.
  Don,
}

export interface DeckPagedItem {
  game: GameId;
  createdAt: string;
  userId: string;
  format: string;
  updatedAt: string;
  legalities: Legalities[];
  colors: string[];
  name: string;
  id: string;
  list?: ShortTradeList;
  user?: ShortUser;
  visibility?: DeckVisibilityEnum;
  featuredCardId?: string;
  archetypes: string[];
  likesCount: number;
  viewsCount: number;
  copiesCount: number;
  commentCount: number;
}

export interface ShortTradeList {
  id: string;
  name: string;
  type: TradeListType;
}

export interface ShortTradeItem {
  id: string;
  /** Every concrete linked trade-item ID represented by the collection row. */
  ids?: string[];
  /** Explicit physical Collection-card ↔ Haves-item relationships. */
  links?: Array<{
    collectionCardId: string;
    tradeItemId: string;
  }>;
  list: ShortTradeList;
}

export const defaultDeckLocations = [
  DeckLocationEnum.Mainboard,
  DeckLocationEnum.Sideboard,
  DeckLocationEnum.Considering,
];

export interface DeckCard extends DeckCardData {
  location: DeckLocationEnum;
  quantity: number;
  isLegal: boolean;
  finish: FinishEnum;
}

export interface DeckCardData {
  id: string;
  oracleId: string;
  game: GameId;
  name: string;
  deckCopyLimit?: {
    /** Omitted when the card permits any number of copies. */
    max?: number;
  };
  types: string[];
  costValue: number;
  costPrinted: string;
  finishes: FinishEnum[];
  printings: string[];
  price?: EmbeddedCardPrice;
  printing?: CardPrinting;
  colorIdentity: ManaColorEnum[];
  colors: ManaColorEnum[];
}

export interface DeckValidation {
  valid: boolean;
  issues: string[];
}

export interface DeckDetails {
  user: ShortUser;
  userId: string;
  createdAt: string;
  format: string;
  updatedAt: string;
  visibility: DeckVisibilityEnum;
  name: string;
  id: string;
  cards: DeckCard[];
  list?: ShortTradeList;
  description: string;
  descriptionHold?: boolean;
  game: GameId;
  pricing: PricingConfiguration;
  archetypes: string[];
  keyCards: Record<string, string>;
  featuredCardId?: string;
  likesCount: number;
  viewsCount: number;
  copiesCount: number;
  commentCount: number;
  isLiked: boolean;
  // Validation is the per-game submission readiness report computed
  // server-side and refreshed on every read/mutation. Powers the
  // DeckStatusBadge and gates the publish (Public visibility) flow.
  validation?: DeckValidation;
}

export enum MagicFormatEnum {
  Brawl = "brawl",
  Commander = "commander",
  Duel = "duel",
  Future = "future",
  Frontier = "frontier",
  Historic = "historic",
  Legacy = "legacy",
  Modern = "modern",
  Oathbreaker = "oathbreaker",
  Pauper = "pauper",
  Penny = "penny",
  Pioneer = "pioneer",
  Standard = "standard",
  Vintage = "vintage",
}

export enum OnePieceFormatEnum {
  Standard = "standard",
  Eternal = "eternal",
}

// Slug is "standard"; the display label is "Constructed" (see formatLabel).
export enum RiftboundFormatEnum {
  Constructed = "standard",
}

export interface CardCodeEntry {
  setCode: string;
  number: string;
  quantity: number;
  location: string;
}

export interface DeckCreateRequest {
  name: string;
  format?: string;
  list: string;
  cheapest: boolean;
  game: string;
  cardCodes?: CardCodeEntry[];
}

export interface DeckAddCardRequest {
  location: DeckLocationEnum;
  cardId: string;
  quantity: number;
  finish?: FinishEnum;
}

export const addCardFormDataSchema = z.object({
  deckId: z.string().min(1, "Deck ID is required"),
  cardId: z.string().min(1, "Card ID is required"),
  location: z.coerce
    .number()
    .int("Location must be an integer")
    .min(0, "Invalid location")
    .max(5, "Invalid location"),
  quantity: z.coerce
    .number()
    .int("Quantity must be a whole number")
    .min(1, "Quantity must be at least 1"),
  finish: z.string().optional(),
});

export interface DeckRemoveCardRequest {
  location: DeckLocationEnum;
  cardId: string;
  finish?: FinishEnum;
}

export interface IdentifiableCard {
  cardId: string;
  quantity: number;
  location: DeckLocationEnum;
  finish?: FinishEnum;
}

export interface DeckUpdateCardRequest {
  current: IdentifiableCard;
  next: IdentifiableCard;
}

export interface DeckSplitTarget {
  cardId: string;
  finish: FinishEnum;
  quantity: number;
}

export interface DeckSplitSource {
  cardId: string;
  finish: FinishEnum;
}

export interface DeckSplitCardRequest {
  oracleId: string;
  location: DeckLocationEnum;
  sources: DeckSplitSource[];
  targets: DeckSplitTarget[];
}

export enum DeckVisibilityEnum {
  _,
  Private,
  Public,
}

export interface DeckUpdateRequest {
  name: string;
  visibility: DeckVisibilityEnum;
  format: string;
  description: string;
  archetypes?: string[];
  keyCards?: Record<string, string>;
}

export enum DeckCardSortOptions {
  Name = "name",
  ManaValue = "manaValue",
  Price = "price",
}

export enum DeckSortOptions {
  CreatedAt = "createdAt",
  UpdatedAt = "updatedAt",
  Name = "name",
  Likes = "likes",
}

export interface DecksPagedRequest {
  userId: string;
  query: string;
  page: number;
  perPage: number;
  sort: DeckSortOptions;
  order: SortOrderEnum;
  formatFilter: string;
  visibility?: DeckVisibilityEnum;
  games?: GameId[];
}

export interface DecksPagedResponse {
  decks: DeckPagedItem[];
  hasMore: boolean;
}

export interface DeckDeleteResponse {
  listId: string;
}

export interface DecksLatestPublicRequest {
  page: number;
  perPage: number;
  format?: string;
  userId?: string;
  game?: string;
}

export interface DeckCopyRequest {
  deckId: string;
}
